import { useMemo } from 'react';
import { term, termTitle, type TermKey } from '@oip/contracts';
import { displayLocale } from '../utils/format';

/**
 * The reader's own word for an everyday fleet noun.
 *
 * ⚠️ **Reads the SAME module value the date formatters do**, set once by `useDisplayLocale`
 * in the shell, rather than calling `useUnits` itself. Two reasons, and the second is the
 * one that decided it:
 *
 * - One source. A second read of "which locale is this" is how a column header comes to say
 *   "license plate" while the field below it still says "registration".
 * - It keeps screens UNCOUPLED from the units query. Going through `useUnits` made every
 *   screen that names a plate depend transitively on `useTenantKey`, which broke eight
 *   existing cases whose `hooks/api` mock had no reason to know about it. A display
 *   preference should not make a vehicle form harder to test.
 *
 * ⚠️ Reactivity comes from the shell KEYING the routed area on the locale, exactly as the
 * dates do — so a saved change repaints these words too rather than leaving a stale one
 * beside a fresh one.
 */
export function useTerm(): { t: (key: TermKey) => string; T: (key: TermKey) => string } {
  const locale = displayLocale();
  /*
   * ⚠️ MEMOISED on the locale, so `t` and `T` keep a stable identity between renders.
   * Column lists are built inside `useMemo`, and a helper that changed identity every
   * render would either rebuild those on every keystroke or — worse — be left out of the
   * dependency list to avoid that, which is how a memoised header keeps the old word after
   * the locale changes.
   */
  return useMemo(
    () => ({ t: (key: TermKey) => term(key, locale), T: (key: TermKey) => termTitle(key, locale) }),
    [locale],
  );
}
